import { useCallback, useEffect, useMemo, useState } from 'react';

import { hrApi } from '@/api';

import { EmployeeSelect } from '@/components/selects';

import { PageHeader } from '@/components/layout/PageHeader';

import { Button } from '@/components/ui/Button';

import { Input } from '@/components/ui/Input';

import { DatePicker } from '@/components/ui/DatePicker';

import { Select } from '@/components/ui/Select';

import { showApiError } from '@/utils/apiError';

import { DataTable, type Column } from '@/components/ui/DataTable';
import { RecordDataTable } from '@/components/ui/RecordDataTable';
import { notify } from '@/core/toast/notify';



type CompensationStructure = { id: number; name?: string; structureName?: string };



export function HrCompensationPage() {

  const [structures, setStructures] = useState<CompensationStructure[]>([]);

  const [structureName, setStructureName] = useState('');

  const [employeeId, setEmployeeId] = useState('');

  const [structureId, setStructureId] = useState('');

  const [gross, setGross] = useState('');

  const [effectiveFrom, setEffectiveFrom] = useState('');

  const [assignments, setAssignments] = useState<unknown[]>([]);



  const structureOptions = useMemo(

    () =>

      structures.map((s) => ({

        value: String(s.id),

        label: s.name ?? s.structureName ?? `Structure #${s.id}`,

      })),

    [structures],

  );



  const load = useCallback(async () => {

    try {

      setStructures((await hrApi.listCompensationStructures()) as CompensationStructure[]);

    } catch (err) {

      showApiError(err, 'Failed to load structures');

    }

  }, []);



  useEffect(() => {

    load();

  }, [load]);



  const createStructure = async () => {

    try {

      await hrApi.createCompensationStructure(structureName, 'INR', 'IN');

      notify.success('Structure created');

      load();

    } catch (err) {

      showApiError(err, 'Failed to create structure');

    }

  };



  const assign = async () => {

    try {

      await hrApi.assignCompensation({

        employeeId: Number(employeeId),

        structureId: Number(structureId),

        effectiveFrom,

        grossAmount: Number(gross),

      });

      notify.success('Salary assigned');

      setAssignments(await hrApi.listCompensationAssignments(Number(employeeId)));

    } catch (err) {

      showApiError(err, 'Assignment failed');

    }

  };



  const structureColumns: Column<CompensationStructure>[] = [
    { key: 'id', header: '#' },
    { key: 'name', header: 'Name', render: (row) => row.name ?? row.structureName ?? '—' },
    { key: 'structureName', header: 'Structure name', render: (row) => row.structureName ?? '—' },
  ];

  return (

    <div>

      <PageHeader title="Compensation" description="Salary structures and employee assignments" breadcrumbs={[{ label: 'HR', to: '/hr' }, { label: 'Compensation' }]} />

      <div className="grid gap-4 lg:grid-cols-2">

        <section className="card space-y-3 p-4">

          <h3 className="font-semibold">Salary structures</h3>

          <Input label="Structure name" value={structureName} onChange={(e) => setStructureName(e.target.value)} />

          <Button onClick={() => void createStructure()}>Create structure</Button>
          <DataTable columns={structureColumns} data={structures} keyField="id" embedded emptyTitle="No structures yet" />

        </section>

        <section className="card space-y-3 p-4">

          <h3 className="font-semibold">Assign to employee</h3>

          <EmployeeSelect value={employeeId} onChange={setEmployeeId} />

          <Select

            label="Salary structure"

            placeholder="Select structure"

            value={structureId}

            onChange={setStructureId}

            options={structureOptions}

          />

          <Input label="Gross amount" value={gross} onChange={(e) => setGross(e.target.value)} />

          <DatePicker label="Effective from" value={effectiveFrom} onChange={setEffectiveFrom} />

          <Button onClick={() => void assign()}>Assign salary</Button>
          <RecordDataTable data={assignments} embedded emptyTitle="No assignments for this employee yet" />

        </section>

      </div>

    </div>

  );

}


